import React from 'react'
import { Box, Button, IconButton, Typography } from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import AddIcon from '@mui/icons-material/Add'
import DisabledReason from '../system/DisabledReason'
import { HAIRLINE, INNER_RADIUS, PILL_RADIUS } from '../../agent/components/surface'
import { NOTE_TYPE_LABELS, notesFor } from '../../utils/guided'
import type { GuideNoteView, GuidePoint } from '../../types/gotypes'

export interface GuideNotesPanelProps {
  points: GuidePoint[]
  notes: GuideNoteView[]
  /** Why the person may not change the notes; undefined when they may. */
  disabledReason?: string
  onAdd: (pointId: string | null) => void
  onEdit: (note: GuideNoteView) => void
}

/** Who last wrote it, in a few words. */
export function noteByline(note: GuideNoteView): string {
  if (note.person_edited_at) return 'Edited by you'
  return note.origin === 'person' ? 'Added by you' : 'Written by the agent'
}

function NoteCard({ note, disabledReason, onEdit }: { note: GuideNoteView; disabledReason?: string; onEdit: (note: GuideNoteView) => void }) {
  return (
    <Box
      data-testid={`guide-note-${note.id}`}
      sx={{ border: HAIRLINE, borderRadius: INNER_RADIUS, backgroundColor: 'background.paper', px: 1.25, py: 1 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary' }}>
            {NOTE_TYPE_LABELS[note.type] ?? note.type}
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', lineHeight: 1.35 }}>{note.title}</Typography>
        </Box>
        <DisabledReason reason={disabledReason}>
          <IconButton size="small" title="Edit note" aria-label={`Edit note: ${note.title}`} onClick={() => onEdit(note)} sx={{ color: 'info.main', mt: -0.5, mr: -0.5 }}>
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DisabledReason>
      </Box>
      <Typography
        sx={{
          mt: 0.5, fontSize: 12.5, color: 'text.primary', lineHeight: 1.45, whiteSpace: 'pre-wrap',
          display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}
      >
        {note.body}
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: 11, color: 'text.secondary' }}>
        {noteByline(note)}
        {note.source_files.length > 0 && ` · From ${note.source_files.join(', ')}`}
      </Typography>
    </Box>
  )
}

/**
 * What was distilled so far, one group per agenda point and one for the rest.
 * The person corrects a note with Edit; a note they edit is theirs, and the
 * agent writes a new one rather than change it.
 */
const GuideNotesPanel: React.FC<GuideNotesPanelProps> = ({ points, notes, disabledReason, onAdd, onEdit }) => {
  const groups = [
    ...points.map((p, i) => ({ key: p.id, pointId: p.id as string | null, title: `${i + 1}. ${p.title}`, notes: notesFor(notes, p.id) })),
    { key: '_other', pointId: null as string | null, title: 'Other notes', notes: notesFor(notes, null) },
  ].filter((g) => g.notes.length > 0)

  return (
    <Box data-testid="guide-notes" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
          Notes ({notes.length})
        </Typography>
        <DisabledReason reason={disabledReason}>
          <Button size="small" color="info" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={() => onAdd(null)} sx={{ textTransform: 'none', fontSize: 12, borderRadius: PILL_RADIUS }}>
            Add a note
          </Button>
        </DisabledReason>
      </Box>
      {groups.length === 0 && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          The agent writes a note here as soon as you tell it something. You can correct any note.
        </Typography>
      )}
      {groups.map((g) => (
        <Box key={g.key} component="section" aria-label={g.title} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>{g.title}</Typography>
          {g.notes.map((n) => <NoteCard key={n.id} note={n} disabledReason={disabledReason} onEdit={onEdit} />)}
        </Box>
      ))}
    </Box>
  )
}

export default GuideNotesPanel
